import { observer } from 'mobx-react-lite'
import moment from 'moment'
import { Button } from 'app/components/ui/button'
import { type Condition, type ConditionMatch, conditionScope } from 'app/lib/workflows/conditions'
import {
  type ContactField,
  availableFieldsForTrigger,
  isDateOnlyAnchor,
  triggerConditionFieldsByScope,
} from 'app/lib/workflows/dataContext'
import { useDateTriggerFields } from '../useDateTriggerFields'
import { REENROLLMENT_CONSEQUENCE, REENROLLMENT_POLICIES } from 'app/lib/workflows/actionCatalogs'
import { isDateAnchored } from 'app/lib/workflows/serialize'
import {
  ACTIVITY_TYPES,
  NEW_TEXT_CONVERSATION,
  TRIGGER_SOURCE_OPTIONS,
  type TriggerSource,
  triggerDef,
  triggerEffectiveSource,
  triggerSourceFilterable,
} from 'app/lib/workflows/triggerCatalog'
import type { DateOffsetUnit, ReenrollmentPolicy, TriggerNodeData } from 'app/types/workflows'
import { useFormOptions } from '../useFormOptions'
import { Alert } from './Alert'
import { AvailableData } from './AvailableData'
import { ConditionEditor } from './ConditionEditor'
import { Disclosure } from './Disclosure'
import { DateInput } from './DateInput'
import { ChoiceSelect, type Choice, Field, InfoHint, MultiSelect, ToggleField } from './fields'
import { type Panel, type SetFn, useConditionValueOptions } from './panelShared'
import { Section } from './Section'
import { TreatmentTriggerFields } from './TreatmentTriggerFields'
import type { TriggerLookback } from '../useTriggerLookback'

const ACTIVITY_TYPE_CHOICES: Choice[] = ACTIVITY_TYPES.map((type) => ({
  value: type === `Any activity` ? `` : type,
  label: type,
}))

/** Scope step for Form-scoped triggers: pick 1+ of the practice's real forms, derived from activity
 *  history. The selected forms' shared fields become downstream data. Its own component so the forms
 *  query only runs when a Form trigger is open. */
function FormScopeField({ data, set }: { data: TriggerNodeData; set: SetFn }) {
  const { forms, loading } = useFormOptions()
  const selectedIds = (data.formScope ?? []).map((form) => form.typeId)
  const choices: Choice[] = forms.map((form) => ({ value: form.typeId, label: form.title }))
  const onChange = (ids: string[]) => {
    const selected = forms.filter((form) => ids.includes(form.typeId))
    // Derived data = fields shared by ALL selected forms (intersection), so a downstream step never
    // references a field some submissions lack. No selection = any form, no derived fields.
    const formFields = selected.length
      ? selected.map((form) => form.fields).reduce((shared, next) => shared.filter((field) => next.includes(field)))
      : []
    set({
      formScope: selected.map((form) => ({ typeId: form.typeId, title: form.title, formIds: form.formIds })),
      formFields,
    })
  }
  return (
    <Field
      label="Form"
      info="Leave empty for any form. The fields the chosen forms collect become data for later steps."
      hint={!loading && forms.length === 0 ? `No submissions yet. This list builds from forms you receive.` : undefined}
    >
      <MultiSelect
        choices={choices}
        selected={selectedIds}
        onChange={onChange}
        placeholder="Any form"
        loading={loading}
      />
    </Field>
  )
}

const OFFSET_UNITS: { value: DateOffsetUnit; singular: string; plural: string }[] = [
  { value: `m`, singular: `minute`, plural: `minutes` },
  { value: `h`, singular: `hour`, plural: `hours` },
  { value: `d`, singular: `day`, plural: `days` },
  { value: `w`, singular: `week`, plural: `weeks` },
  { value: `mo`, singular: `month`, plural: `months` },
  { value: `y`, singular: `year`, plural: `years` },
]

const OFFSET_SELECT_CLASS = `h-7 w-auto gap-1.5 rounded-md px-2 py-1 text-xs shadow-none`

/** Fetches date fields from the API and passes them to DateSchedule. */
function DateScheduleWithFields({
  data,
  set,
  sourceType,
}: {
  data: TriggerNodeData
  set: (patch: Partial<TriggerNodeData>) => void
  sourceType: 'Contact' | 'Appointment'
}) {
  const dateFields = useDateTriggerFields(sourceType)
  return <DateSchedule data={data} set={set} dateFields={dateFields} />
}

/** The schedule for a date-anchored trigger: which date to count from, and every moment to fire.
 *
 *  A list rather than a fixed ladder, because a practice that wants five nudges at 5, 4, 3, 2 and 1
 *  days out should be able to say so. Each row becomes one `date_reached` filter and they are ORed,
 *  so all of this is one workflow.
 *
 *  Zero is deliberately not offerable: the engine rejects a zero offset ("Offset amount must be
 *  non-zero"), and offsets count from the appointment's TIME, so "the morning of" is genuinely a
 *  number of hours before rather than a day-of concept. */
function DateSchedule({
  data,
  set,
  dateFields,
}: {
  data: TriggerNodeData
  set: (patch: Partial<TriggerNodeData>) => void
  dateFields: ContactField[]
}) {
  const offsets = data.dateOffsets ?? []
  const update = (index: number, patch: Partial<(typeof offsets)[number]>) =>
    set({ dateOffsets: offsets.map((offset, i) => (i === index ? { ...offset, ...patch } : offset)) })
  // Date-only anchors (hygiene due date, birthday) have no time of day, so "on the day" is a real
  // choice, encoded as +1h after (the engine rejects zero) and held by the send window until
  // morning. The row SAYS "on the day" instead of leaking that encoding (UX audit C2). Appointment
  // anchors carry real times, so their offsets stay literal: "3 hours before" a 2pm visit is 11am,
  // and pretending it's "the morning of" would lie for every afternoon appointment.
  const dateOnly = isDateOnlyAnchor(data.dateField)
  const isOnDay = (offset: (typeof offsets)[number]) =>
    dateOnly && offset.amount === 1 && offset.unit === `h` && offset.direction === `after`

  return (
    <Section title="Schedule">
      <Field label="Count from" error={data.dateField ? undefined : `Choose which date this counts from.`}>
        <ChoiceSelect
          value={data.dateField ?? ``}
          choices={dateFields.map((field) => ({ value: field.key, label: field.label }))}
          placeholder="Select a date"
          invalid={!data.dateField}
          onChange={(value) => set({ dateField: value })}
        />
      </Field>
      <Field
        label="Send at"
        info="Each row is one send, counted from that date. They all belong to this one workflow."
        error={offsets.length ? undefined : `Add at least one moment, or this will never run.`}
      >
        <div className="flex flex-col gap-1.5">
          {offsets.map((offset, index) => (
            <div key={index} className="flex items-center gap-1.5">
              {!isOnDay(offset) && (
                <>
                  <input
                    type="number"
                    min={1}
                    value={offset.amount}
                    aria-label={`Amount ${index + 1}`}
                    onChange={(e) => update(index, { amount: Math.max(1, Number(e.target.value) || 1) })}
                    className="w-16 rounded-md border border-default px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
                  />
                  {/* Reads back as a sentence, so "1 day before" rather than "1 days before". */}
                  <ChoiceSelect
                    value={offset.unit}
                    ariaLabel={`Unit ${index + 1}`}
                    onChange={(value) => update(index, { unit: value as DateOffsetUnit })}
                    choices={OFFSET_UNITS.map((unit) => ({
                      value: unit.value,
                      label: offset.amount === 1 ? unit.singular : unit.plural,
                    }))}
                    className={OFFSET_SELECT_CLASS}
                  />
                </>
              )}
              <ChoiceSelect
                value={isOnDay(offset) ? `on_day` : offset.direction}
                ariaLabel={`Direction ${index + 1}`}
                onChange={(value) =>
                  value === `on_day`
                    ? update(index, { amount: 1, unit: `h`, direction: `after` })
                    : update(index, {
                        direction: value as `before` | `after`,
                        // Leaving "on the day" restores a sensible offset instead of "1 hour before".
                        ...(isOnDay(offset) ? { amount: 1, unit: `d` as DateOffsetUnit } : {}),
                      })
                }
                choices={[
                  { value: `before`, label: isOnDay(offset) ? `days before` : `before` },
                  { value: `after`, label: isOnDay(offset) ? `days after` : `after` },
                  ...(dateOnly ? [{ value: `on_day`, label: `on the day` }] : []),
                ]}
                className={OFFSET_SELECT_CLASS}
              />
              {isOnDay(offset) && <span className="text-[11px] text-minimal">when the send window opens</span>}
              <Button
                variant="subtle"
                size="xs"
                aria-label={`Remove moment ${index + 1}`}
                onClick={() => set({ dateOffsets: offsets.filter((_, i) => i !== index) })}
                className="ml-auto"
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="dashed"
            size="xs"
            onClick={() => set({ dateOffsets: [...offsets, { amount: 1, unit: `d`, direction: `before` }] })}
            className="self-start"
          >
            + Add a moment
          </Button>
        </div>
      </Field>
    </Section>
  )
}

/** One of the trigger's two condition groups. A plain heading with the explanation behind its "i",
 *  then flat rows and an add link. Which group a row lives in IS its scope. */
function ConditionGroup({
  heading,
  info,
  consequence,
  children,
}: {
  heading: string
  info: string
  /** One line that changes the choice being made right now; shown only while it applies. */
  consequence?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center text-xs font-medium text-subtle">
        {heading}
        <InfoHint text={info} />
      </div>
      {consequence && <div className="text-[11px] text-amber-700">{consequence}</div>}
      {children}
    </div>
  )
}

/** The trigger's two condition groups. Each edits its own partition AND its own all/any, because
 *  they answer different questions. Missed-call text-back is the case that forced it: enroll on ANY
 *  of two ways a call goes unanswered, but ONLY IF the call was inbound. One shared setting cannot
 *  express that, and the version that drops the inbound guard texts people the practice rang. */
function TriggerConditions({
  data,
  set,
  conditionFields,
  stateFields,
  valueOptions,
  blunt,
  created,
  recordNoun,
}: {
  data: TriggerNodeData
  set: (patch: Partial<TriggerNodeData>) => void
  conditionFields: string[]
  stateFields: string[]
  valueOptions: Record<string, Choice[]>
  blunt: boolean
  created: boolean
  recordNoun: string
}) {
  const conditions = data.conditions ?? []
  const changeConditions = conditions.filter((c) => conditionScope(c) === `change`)
  const stateConditions = conditions.filter((c) => conditionScope(c) !== `change`)
  // Each group edits its own partition; writes reassemble the full list (order is cosmetic, since
  // serialize re-splits by scope anyway).
  const setPartition =
    (other: Condition[], key: `match` | `stateMatch`) =>
    (patch: { match?: ConditionMatch; conditions?: Condition[] }) => {
      const next: Partial<TriggerNodeData> = {}
      if (patch.conditions) next.conditions = [...patch.conditions, ...other]
      if (patch.match) next[key] = patch.match
      set(next)
    }

  return (
    <Section title="Conditions">
      <ConditionGroup
        heading={created ? `When it's created with` : `When it changes`}
        info={
          created
            ? `The values the new ${recordNoun} was saved with. Add one to fire only on the ones that matter.`
            : `The change itself: the value the event just wrote. Add one to fire only on the moments that matter.`
        }
        consequence={blunt && changeConditions.length === 0 ? `Any edit counts until you add a change.` : undefined}
      >
        <ConditionEditor
          match={data.match ?? `all`}
          conditions={changeConditions}
          onChange={setPartition(stateConditions, `match`)}
          availableFields={conditionFields}
          valueOptions={valueOptions}
          fixedScope="change"
          changeWording={!created}
          label={null}
          addLabel="Add a change"
        />
      </ConditionGroup>
      <ConditionGroup heading={`And the ${recordNoun}`} info={`Checked on the live ${recordNoun} at that moment.`}>
        <ConditionEditor
          match={data.stateMatch ?? data.match ?? `all`}
          conditions={stateConditions}
          onChange={setPartition(changeConditions, `stateMatch`)}
          availableFields={stateFields}
          valueOptions={valueOptions}
          fixedScope="state"
          label={null}
          addLabel="Add a check"
        />
      </ConditionGroup>
    </Section>
  )
}

const DAY = `YYYY-MM-DD`

/** The backfill switch, and what going live would replay. */
export type BackfillControl = {
  on: boolean
  onChange: (on: boolean) => void
  /** Already live: going live has happened, so there is nothing left to preview. */
  live: boolean
  /** The past events going live would enroll. Absent while there is nothing to preview. */
  preview?: Pick<TriggerLookback, `status` | `total` | `loadedAt`> & { onView: () => void }
}

/** "N events since Oct 5 will enroll when you turn this on", with the way into the list. */
function BackfillPreview({
  preview,
  since,
  dateTrigger,
}: {
  preview: NonNullable<BackfillControl[`preview`]>
  since: string
  dateTrigger: boolean
}) {
  if (preview.status === `error`) return <p className="text-xs text-danger-strong">Couldn&rsquo;t count past events.</p>
  if (!preview.loadedAt) return <p className="text-xs text-subtle">Counting past events…</p>
  const day = moment(since).format(`MMM D, YYYY`)
  const count = preview.total.toLocaleString()
  return (
    <p className="text-xs leading-relaxed text-default">
      {dateTrigger
        ? `${count} ${preview.total === 1 ? `person` : `people`} came due since ${day} and will enroll when you turn this on.`
        : `${count} ${preview.total === 1 ? `event` : `events`} since ${day} will enroll when you turn this on.`}
      {preview.total > 0 && (
        <Button variant="link" size="inline" className="ml-1 text-xs" onClick={preview.onView}>
          View
        </Button>
      )}
    </p>
  )
}

export const TriggerPanel = observer(function TriggerPanel({
  data,
  set,
  triggerStartDate,
  onTriggerStartDateChange,
  backfill,
}: Panel<TriggerNodeData> & {
  triggerStartDate: string
  onTriggerStartDateChange: (value: string) => void
  backfill: BackfillControl
}) {
  const def = triggerDef(data.kind)
  const triggerFields = availableFieldsForTrigger(data)
  const fieldsByScope = triggerConditionFieldsByScope(data)
  // Fixed-vocabulary + record-backed value pickers for the entry-condition editor.
  const valueOptions = useConditionValueOptions(triggerFields)
  // Conditional triggers (calls, generic activities) may not resolve to a contact. They get the
  // "require a matched contact" toggle and the "contact fields not in scope" note.
  const isConditional = def.contactProvision === `conditional`
  // Named for what happened, so it carries no contact filter: its contact steps just skip.
  const offersContactMatch = isConditional && data.kind !== NEW_TEXT_CONVERSATION
  const effectiveSource = triggerEffectiveSource(data.kind, data.source)
  // Manual enrollment has no event to condition on. Conditions and source hide; re-enrollment stays
  // (the engine applies it to pushed enrollments too, verified live 08-12).
  const isManual = data.kind === `manual`
  const dateTrigger = isDateAnchored(data.kind)
  const showForm = def.object === `form` || (def.configurableActivityType && data.activityType === `Form`)
  const hasTriggerControls = def.configurableActivityType || showForm || triggerSourceFilterable(data.kind)

  return (
    <>
      {def.status === `backend` && (
        <div className="pb-4">
          <Alert tone="warn">
            Needs {def.backendNote ?? `backend support`} before it can go live. Build and save it now.
          </Alert>
        </div>
      )}
      {hasTriggerControls && (
        <Section title="Trigger">
          {def.configurableActivityType && (
            <Field label="Activity type">
              <ChoiceSelect
                value={data.activityType ?? ``}
                choices={ACTIVITY_TYPE_CHOICES}
                // Changing the type invalidates any form scope picked for the previous type.
                onChange={(v) => set({ activityType: v || undefined, formScope: undefined, formFields: undefined })}
              />
            </Field>
          )}
          {showForm && <FormScopeField data={data} set={set} />}
          {triggerSourceFilterable(data.kind) && (
            <Field
              label="Changed by"
              info="Contacts change from staff edits and from background syncs and integrations. Choose which count."
            >
              <ChoiceSelect
                value={effectiveSource}
                choices={TRIGGER_SOURCE_OPTIONS}
                onChange={(v) => set({ source: v as TriggerSource })}
              />
            </Field>
          )}
        </Section>
      )}
      {def.object === `treatment_plan` && <TreatmentTriggerFields data={data} set={set} />}
      {isDateAnchored(data.kind) && (
        <DateScheduleWithFields
          data={data}
          set={set}
          sourceType={def.object === `appointment` ? `Appointment` : `Contact`}
        />
      )}
      {isManual ? (
        <Section title="Enrollment">
          <div className="flex items-center text-sm text-default">
            Added by you
            <InfoHint text="Open a contact in the Runs panel and enroll them, or select contacts in the contacts table and use a bulk action. Nothing enrolls on its own." />
          </div>
        </Section>
      ) : (
        <TriggerConditions
          data={data}
          set={set}
          conditionFields={fieldsByScope.change}
          stateFields={fieldsByScope.state}
          valueOptions={valueOptions}
          blunt={!!def.blunt}
          created={def.lifecycle === `created`}
          recordNoun={def.objectLabel.toLowerCase()}
        />
      )}
      <Section title="Re-enrollment">
        <Field label="Repeat runs" info={REENROLLMENT_CONSEQUENCE[data.reenrollment]}>
          <ChoiceSelect
            value={data.reenrollment}
            choices={REENROLLMENT_POLICIES}
            onChange={(v) => set({ reenrollment: v as ReenrollmentPolicy })}
          />
        </Field>
      </Section>
      {!isManual && (
        <Section title="Past events">
          <ToggleField
            label="Backfill past events"
            info={
              dateTrigger
                ? `Off, the workflow reaches people whose date comes due after you turn it on. On, it also reaches people whose date came due since the day you pick.`
                : `Off, the workflow starts with events from the moment you turn it on. On, it also runs for matching events since the day you pick.`
            }
            hint={
              backfill.live && !backfill.on
                ? `Started from ${moment(triggerStartDate).format(`MMM D, YYYY`)}.`
                : undefined
            }
            checked={backfill.on}
            onChange={backfill.onChange}
          />
          {backfill.on && (
            <>
              <Field
                label={dateTrigger ? `Dates since` : `Events since`}
                hint={backfill.live ? `Moving this earlier runs the ones in between when you save.` : undefined}
              >
                <DateInput
                  aria-label={dateTrigger ? `Dates since` : `Events since`}
                  value={moment(triggerStartDate).format(DAY)}
                  // A day, not an instant: the start of that day on this browser's clock. Nobody was
                  // choosing 12:27 PM on purpose, and the calendar is the app's one date grammar.
                  onChange={(day) => onTriggerStartDateChange(moment(day, DAY).startOf(`day`).toISOString())}
                />
              </Field>
              {backfill.preview && (
                <BackfillPreview preview={backfill.preview} since={triggerStartDate} dateTrigger={dateTrigger} />
              )}
            </>
          )}
        </Section>
      )}
      {offersContactMatch && (
        <Disclosure title="Advanced" modified={!!data.requireContactMatch}>
          <ToggleField
            label="Require a matched contact"
            info="Activities like calls don't always resolve to a contact. With this on, steps that act on the contact always have one; without it, those steps are skipped when there's no contact."
            checked={data.requireContactMatch ?? false}
            onChange={(checked) => set({ requireContactMatch: checked })}
          />
        </Disclosure>
      )}
      <div className="pt-4">
        <AvailableData
          fields={triggerFields}
          title="Data available to later steps"
          note={
            offersContactMatch && !data.requireContactMatch
              ? `Contact fields need “Require a matched contact” under Advanced.`
              : undefined
          }
        />
      </div>
    </>
  )
})
